import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import firebase from 'firebase';
import { LocalSessionService } from 'src/services/local-session.service';
import { AuthService } from 'src/services/auth.service';
import { HttpClient } from '@angular/common/http';
import { RouteHelperService } from 'src/services/route-helper.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-public-auth',
  templateUrl: './public-auth.component.html',
  styleUrls: ['./public-auth.component.css'],
  encapsulation: ViewEncapsulation.None
})
export class PublicAuthComponent implements OnInit {

  isLogin: boolean = true;
  phoneForm: FormGroup;
  registerForm: FormGroup;
  errorMessage: string = '';
  successMessage: string = '';
  isLoading: boolean = false;
  reCaptchaVerifier: any;
  private vehicleCheckCancelled: boolean = false;

  constructor(
    private localSessionService: LocalSessionService,
    private authService: AuthService,
    private http: HttpClient,
    private router: Router,
    public routeHelper: RouteHelperService
  ) { }

  ngOnInit(): void {
    this.initializeForms();
    this.setupReCaptcha();
  }

  initializeForms() {
    // Phone form for login
    this.phoneForm = new FormGroup({
      phoneNumber: new FormControl('', [
        Validators.required, 
        Validators.pattern(/^9\d{9}$/)
      ])
    });

    // Registration form
    this.registerForm = new FormGroup({
      phoneNumber: new FormControl('', [
        Validators.required, 
        Validators.pattern(/^9\d{9}$/)
      ]),
      email: new FormControl('', [
        Validators.required, 
        Validators.email,
        Validators.pattern('[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}')
      ])
    });

    // Format phone number input
    this.phoneForm.get('phoneNumber')?.valueChanges.subscribe(value => {
      this.formatPhoneNumber(value, this.phoneForm.get('phoneNumber'));
    });

    this.registerForm.get('phoneNumber')?.valueChanges.subscribe(value => {
      this.formatPhoneNumber(value, this.registerForm.get('phoneNumber'));
    });
  }

  formatPhoneNumber(value: string, control: any) {
    if (value) {
      let cleaned = value.replace(/\D/g, '');
      if (cleaned.length > 10) {
        cleaned = cleaned.substring(0, 10);
      }
      if (cleaned.charAt(0) === '0') {
        cleaned = cleaned.slice(1);
      }
      control?.patchValue(cleaned, { emitEvent: false });
    }
  }

  setupReCaptcha() {
    setTimeout(() => {
      if (!this.reCaptchaVerifier) {
        this.reCaptchaVerifier = new firebase.auth.RecaptchaVerifier('recaptcha-container', {
          size: 'invisible',
          callback: (response) => {
            console.log('reCAPTCHA solved');
          },
          'expired-callback': () => {
            console.log('reCAPTCHA expired');
          },
          'error-callback': (error) => {
            console.log('reCAPTCHA error:', error);
          }
        });

        this.reCaptchaVerifier.render().catch((error) => {
          console.error('reCAPTCHA render error:', error);
        });
      }
    }, 1000);
  }

  toggleMode() {
    this.isLogin = !this.isLogin;
    this.errorMessage = '';
    this.successMessage = '';
    this.phoneForm.reset();
    this.registerForm.reset();
  }

  async loginWithPhone() {
    if (this.phoneForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        const phoneNumber = this.phoneForm.get('phoneNumber')?.value;
        const fullPhoneNumber = '+63' + phoneNumber;
        
        // Check for existing valid local session first
        const existingSession = this.localSessionService.hasValidSession(fullPhoneNumber);
        if (existingSession) {
          this.successMessage = 'Welcome back! Checking your account...';
          // Store in old format for dashboard compatibility
          localStorage.setItem('user', JSON.stringify({
            uid: existingSession.uid,
            phoneNumber: existingSession.phoneNumber,
            displayName: existingSession.displayName,
            email: existingSession.email
          }));
          
          // Check for vehicles and redirect accordingly
          this.vehicleCheckCancelled = false;
          setTimeout(async () => {
            if (!this.vehicleCheckCancelled) {
              await this.checkVehiclesAndRedirect(existingSession.uid);
            }
          }, 1000);
          return;
        }
        
        // No valid session, send OTP
        const confirmationResult = await firebase.auth().signInWithPhoneNumber(fullPhoneNumber, this.reCaptchaVerifier);
        localStorage.setItem('verificationId', JSON.stringify(confirmationResult.verificationId));
        localStorage.setItem('phoneNumber', JSON.stringify(fullPhoneNumber));
        this.router.navigate([this.routeHelper.getRoutes().verifyOtp]);
      } catch (error) {
        console.error('Phone login error:', error);
        this.errorMessage = 'Failed to send OTP. Please try again.';
      } finally {
        this.isLoading = false;
      }
    } else {
      this.validateAllFormFields(this.phoneForm);
    }
  }

  async registerWithPhone() {
    if (this.registerForm.valid) {
      this.isLoading = true;
      this.errorMessage = '';
      
      try {
        // Store registration data temporarily
        const phoneNumber = this.registerForm.get('phoneNumber')?.value;
        const fullPhoneNumber = '+63' + phoneNumber;
        const registrationData = {
          email: this.registerForm.get('email')?.value,
          phoneNumber: fullPhoneNumber // Store the full phone number with country code
        };
        localStorage.setItem('registrationData', JSON.stringify(registrationData));
        
        // Manually handle phone auth to redirect to our public OTP page
        const confirmationResult = await firebase.auth().signInWithPhoneNumber(fullPhoneNumber, this.reCaptchaVerifier);
        localStorage.setItem('verificationId', JSON.stringify(confirmationResult.verificationId));
        localStorage.setItem('phoneNumber', JSON.stringify(fullPhoneNumber));
        this.router.navigate([this.routeHelper.getRoutes().verifyOtp]);
      } catch (error) {
        console.error('Phone registration error:', error);
        this.errorMessage = 'Failed to send OTP. Please try again.';
      } finally {
        this.isLoading = false;
      }
    } else {
      this.validateAllFormFields(this.registerForm);
    }
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      if (control instanceof FormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }


  // Helper methods for form validation display
  isFieldInvalid(form: FormGroup, fieldName: string): boolean {
    const field = form.get(fieldName);
    return field ? field.invalid && (field.dirty || field.touched) : false;
  }

  getFieldError(form: FormGroup, fieldName: string): string {
    const field = form.get(fieldName);
    if (field && field.errors && (field.dirty || field.touched)) {
      if (field.errors['required']) {
        return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is required`;
      }
      if (field.errors['pattern']) {
        if (fieldName === 'phoneNumber') {
          return 'Please enter a valid Philippine mobile number (9xxxxxxxxx)';
        }
        if (fieldName === 'email') {
          return 'Please enter a valid email address';
        }
      }
      if (field.errors['email']) {
        return 'Please enter a valid email address';
      }
      if (field.errors['minlength']) {
        return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
    }
    return '';
  }

  async checkVehiclesAndRedirect(userId: string) {
    try {
      if (this.vehicleCheckCancelled || localStorage.getItem('cancelVehicleCheck') === 'true') {
        localStorage.removeItem('cancelVehicleCheck');
        return;
      }
      
      this.successMessage = 'Loading your vehicles...';
      
      const response = await this.http.post(`${environment.apiBaseUrl}/getUserVehicles`, {
        owner_id: userId
      }).toPromise();
      
      if (this.vehicleCheckCancelled || localStorage.getItem('cancelVehicleCheck') === 'true') {
        localStorage.removeItem('cancelVehicleCheck');
        return;
      }
      
      const userVehicles = response && Array.isArray(response) ? response : [];
      
      if (userVehicles.length > 0) {
        this.successMessage = 'Redirecting to dashboard...';
        setTimeout(() => {
          if (!this.vehicleCheckCancelled && localStorage.getItem('cancelVehicleCheck') !== 'true') {
            this.router.navigate([this.routeHelper.getRoutes().dashboard]);
          } else {
            localStorage.removeItem('cancelVehicleCheck');
          }
        }, 1000);
      } else {
        this.successMessage = 'Setting up your vehicle registration...';
        setTimeout(() => {
          if (!this.vehicleCheckCancelled && localStorage.getItem('cancelVehicleCheck') !== 'true') {
            this.router.navigate([this.routeHelper.getRoutes().vehicleRegistration]);
          } else {
            localStorage.removeItem('cancelVehicleCheck');
          }
        }, 1000);
      }
    } catch (error) {
      if (this.vehicleCheckCancelled || localStorage.getItem('cancelVehicleCheck') === 'true') {
        localStorage.removeItem('cancelVehicleCheck');
        return;
      }
      
      console.error('Error checking user vehicles:', error);
      this.successMessage = 'Redirecting to dashboard...';
      setTimeout(() => {
        if (!this.vehicleCheckCancelled && localStorage.getItem('cancelVehicleCheck') !== 'true') {
          this.router.navigate([this.routeHelper.getRoutes().dashboard]);
        } else {
          localStorage.removeItem('cancelVehicleCheck');
        }
      }, 1000);
    }
  }

  cancelVehicleCheck() {
    this.vehicleCheckCancelled = true;
    this.successMessage = '';
  }
}